import { Link } from 'react-router-dom';
import { useReveal } from '../lib/anim/useReveal';
import type { EngineRecord, EngineStatus } from '../content/engines/index';

const STATUS_PILL_STYLE: Record<EngineStatus, React.CSSProperties> = {
  live: {
    background: 'var(--color-accent)',
    color: '#FFFFFF',
    border: 'none',
  },
  beta: {
    background: 'rgba(11, 18, 32, 0.06)',
    color: 'var(--color-mute)',
    border: '1px solid var(--color-hairline)',
  },
  'coming-soon': {
    background: 'transparent',
    color: 'var(--color-mute)',
    border: '1px solid var(--color-hairline)',
  },
};

const STATUS_LABEL: Record<EngineStatus, string> = {
  live: 'Live',
  beta: 'Beta',
  'coming-soon': 'Coming soon',
};

type EngineCardProps = {
  engine: EngineRecord;
  delay?: 0 | 1 | 2 | 3 | 4 | 5;
};

export function EngineCard({ engine, delay = 0 }: EngineCardProps) {
  const reveal = useReveal({ delay });

  return (
    <div
      ref={reveal.ref}
      {...reveal.props}
      className={`group rounded-xl p-6 flex flex-col gap-4 transition-colors${reveal.props.className ? ' ' + reveal.props.className : ''}`}
      style={{
        border: '1px solid var(--color-hairline)',
        background: 'var(--color-surface)',
      }}
    >
      {/* Header row */}
      <div className="flex items-start justify-between gap-3">
        <h3 className="text-base font-semibold leading-snug" style={{ color: 'var(--color-ink)', fontFamily: 'var(--font-display)' }}>
          {engine.name}
        </h3>
        <span
          className="shrink-0 text-xs px-2 py-0.5 rounded-full font-medium"
          style={{ ...STATUS_PILL_STYLE[engine.status], fontFamily: 'var(--font-mono)' }}
          data-testid={`status-pill-${engine.slug}`}
        >
          {STATUS_LABEL[engine.status]}
        </span>
      </div>

      {/* One-liner */}
      <p className="text-sm leading-relaxed flex-1" style={{ color: 'var(--color-mute)' }}>{engine.oneLiner}</p>

      {/* Links */}
      <div className="flex items-center gap-4 pt-1">
        <Link
          to={`/engines/${engine.slug}`}
          className="text-sm font-medium transition-opacity hover:opacity-70"
          style={{ color: 'var(--color-accent)' }}
        >
          Learn more →
        </Link>
        {engine.liveDemo && (
          <a
            href={engine.liveDemo}
            target="_blank"
            rel="noopener noreferrer"
            className="text-sm transition-colors"
            style={{ color: 'var(--color-mute)' }}
          >
            Live demo ↗
          </a>
        )}
      </div>
    </div>
  );
}
